// Renders the "share your HEARTS engagement" image: the speaker's photo and
// engagement details composited onto speaker_share_template.png. Runs
// entirely client-side on a <canvas> - there is no server-side image
// generation, so this is the only place these coordinates/colors live. They
// were measured directly off the template's pixel data (a fixed 1055x1491
// PNG), not guessed - if the template art changes, these constants need
// re-measuring to match.
//
// Every text row is drawn by fully repainting its rounded box (not just
// stamping text over the template's own placeholder copy like "SPEAKER
// NAME") - painting only the text would leave mismatched placeholder glyphs
// showing through around real content of a different length/script.

export const CARD_W = 1055;
export const CARD_H = 1491;

const PHOTO = { cx: 296, cy: 902, r: 158 };

const NAME_BOX = { x: 77, y: 1017, w: 903, h: 90, r: 26 };
const QUOTE_BOX = { x: 77, y: 1124, w: 903, h: 97, r: 26 };
const TITLE_BOX = { x: 77, y: 1237, w: 519, h: 88, r: 24 };
const TIME_BOX = { x: 613, y: 1238, w: 367, h: 87, r: 24 };
const BADGE_R = 44;

const BOX_FILL = '#EEF0FE';
const NAVY = '#1A0F4B';
const SUBTLE = '#645A93';
const QUOTE_TEXT = '#3B2E82';
const ICON_PURPLE = '#958AFA';

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function paintBox(ctx, box) {
  roundRectPath(ctx, box.x, box.y, box.w, box.h, box.r);
  ctx.fillStyle = BOX_FILL;
  ctx.fill();
}

// Shrinks font-size until `text` fits maxWidth (never below minSize).
function fitFontSize(ctx, text, maxWidth, baseSize, weight, minSize = 13, italic = false) {
  let size = baseSize;
  const style = `${italic ? 'italic ' : ''}${weight} ${size}px Roboto`;
  ctx.font = style;
  while (size > minSize && ctx.measureText(text).width > maxWidth) {
    size -= 1;
    ctx.font = `${italic ? 'italic ' : ''}${weight} ${size}px Roboto`;
  }
  return size;
}

function ellipsize(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1);
  return `${t}…`;
}

export function initialsFrom(name) {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '?';
}

export function loadImage(src, crossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Could not load image: ${src}`));
    img.src = src;
  });
}

function drawPhoto(ctx, img) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(PHOTO.cx, PHOTO.cy, PHOTO.r, 0, Math.PI * 2);
  ctx.clip();
  const scale = Math.max((PHOTO.r * 2) / img.width, (PHOTO.r * 2) / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, PHOTO.cx - dw / 2, PHOTO.cy - dh / 2, dw, dh);
  ctx.restore();
}

function drawInitialsAvatar(ctx, name) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(PHOTO.cx, PHOTO.cy, PHOTO.r, 0, Math.PI * 2);
  ctx.fillStyle = '#DCE3FB';
  ctx.fill();
  ctx.fillStyle = '#6B7FD7';
  ctx.font = `700 ${Math.round(PHOTO.r * 0.8)}px Roboto`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initialsFrom(name), PHOTO.cx, PHOTO.cy + PHOTO.r * 0.06);
  ctx.restore();
}

function drawIconBadge(ctx, cx, cy, kind) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, BADGE_R, 0, Math.PI * 2);
  ctx.fillStyle = ICON_PURPLE;
  ctx.fill();
  ctx.strokeStyle = '#fff';
  ctx.fillStyle = '#fff';
  ctx.lineWidth = Math.max(2.5, BADGE_R * 0.09);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (kind === 'title') {
    const w = BADGE_R * 0.7, h = BADGE_R * 0.9, x0 = cx - w / 2, y0 = cy - h / 2, rr = 4;
    roundRectPath(ctx, x0, y0, w, h, rr);
    ctx.stroke();
    ctx.beginPath();
    [0.32, 0.52, 0.72].forEach((f) => { ctx.moveTo(x0 + w * 0.18, y0 + h * f); ctx.lineTo(x0 + w * 0.82, y0 + h * f); });
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.arc(cx, cy, BADGE_R * 0.55, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx, cy - BADGE_R * 0.32);
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + BADGE_R * 0.22, cy + BADGE_R * 0.06);
    ctx.stroke();
  }
  ctx.restore();
}

function drawQuoteGlyph(ctx, x, y, flip) {
  ctx.save();
  ctx.fillStyle = ICON_PURPLE;
  ctx.font = 'italic 700 64px Georgia, serif';
  ctx.textBaseline = 'middle';
  ctx.textAlign = flip ? 'right' : 'left';
  ctx.fillText(flip ? '”' : '“', x, y);
  ctx.restore();
}

// name: display name. subtitle: "Institution / City, Country" line.
// quote: optional short personal line - the box is left blank (repainted,
// no placeholder) when omitted rather than showing template sample copy.
// talkTitle/talkTime: the engagement's talk (or role/session) and its slot.
export async function renderShareCard(canvas, templateSrc, { photoUrl, name, subtitle, quote, talkTitle, talkTime }) {
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');

  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch (e) { /* proceed with fallback font */ }
  }

  const template = await loadImage(templateSrc);
  ctx.drawImage(template, 0, 0, CARD_W, CARD_H);

  let photoImg = null;
  if (photoUrl) {
    try { photoImg = await loadImage(photoUrl, 'anonymous'); } catch (e) { photoImg = null; }
  }
  if (photoImg) drawPhoto(ctx, photoImg);
  else drawInitialsAvatar(ctx, name);

  // Name / institution box
  paintBox(ctx, NAME_BOX);
  const padX = 40;
  const innerW = NAME_BOX.w - padX * 2;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.fillStyle = NAVY;
  const nameSize = fitFontSize(ctx, name || '', innerW, 34, 700, 18);
  ctx.font = `700 ${nameSize}px Roboto`;
  ctx.fillText(ellipsize(ctx, name || '', innerW), NAME_BOX.x + padX, NAME_BOX.y + 45);
  if (subtitle) {
    ctx.font = '400 20px Roboto';
    ctx.fillStyle = SUBTLE;
    ctx.fillText(ellipsize(ctx, subtitle, innerW), NAME_BOX.x + padX, NAME_BOX.y + 75);
  }

  // Quote box
  paintBox(ctx, QUOTE_BOX);
  if (quote && quote.trim()) {
    const qy = QUOTE_BOX.y + QUOTE_BOX.h / 2;
    drawQuoteGlyph(ctx, QUOTE_BOX.x + 38, qy, false);
    drawQuoteGlyph(ctx, QUOTE_BOX.x + QUOTE_BOX.w - 38, qy, true);
    const qInnerW = QUOTE_BOX.w - 190;
    ctx.textAlign = 'center';
    ctx.fillStyle = QUOTE_TEXT;
    const qSize = fitFontSize(ctx, quote.trim(), qInnerW, 24, 500, 15, true);
    ctx.font = `italic 500 ${qSize}px Roboto`;
    ctx.textBaseline = 'middle';
    ctx.fillText(ellipsize(ctx, quote.trim(), qInnerW), QUOTE_BOX.x + QUOTE_BOX.w / 2, qy + 2);
    ctx.textAlign = 'left';
  }

  // Talk title box
  paintBox(ctx, TITLE_BOX);
  {
    const bcx = TITLE_BOX.x + 68, bcy = TITLE_BOX.y + TITLE_BOX.h / 2;
    drawIconBadge(ctx, bcx, bcy, 'title');
    const tx = bcx + BADGE_R + 26;
    const tw = TITLE_BOX.x + TITLE_BOX.w - 24 - tx;
    ctx.fillStyle = NAVY;
    ctx.textBaseline = 'middle';
    const size = fitFontSize(ctx, talkTitle || '', tw, 24, 600, 15);
    ctx.font = `600 ${size}px Roboto`;
    ctx.fillText(ellipsize(ctx, talkTitle || '', tw), tx, bcy + 1);
  }

  // Talk time box
  paintBox(ctx, TIME_BOX);
  {
    const bcx = TIME_BOX.x + 68, bcy = TIME_BOX.y + TIME_BOX.h / 2;
    drawIconBadge(ctx, bcx, bcy, 'time');
    const tx = bcx + BADGE_R + 26;
    const tw = TIME_BOX.x + TIME_BOX.w - 24 - tx;
    ctx.fillStyle = NAVY;
    ctx.textBaseline = 'middle';
    const size = fitFontSize(ctx, talkTime || '', tw, 22, 600, 14);
    ctx.font = `600 ${size}px Roboto`;
    ctx.fillText(ellipsize(ctx, talkTime || '', tw), tx, bcy + 1);
  }

  return canvas;
}
